import { create } from "zustand";
import { installMode, isIosDevice, shouldShowCard } from "./installRules.ts";

/** Événement de Chrome (non standard) : garde la fenêtre d'installation pour la montrer au bon moment. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const SNOOZE_KEY = "sk-install-later";
const REQUEST_KEY = "sk-install-requested";

// Stockage du téléphone : peut être refusé (navigation privée). La carte marche quand même, sans mémoire.
function get(store: () => Storage, key: string): string | null {
  try {
    return store().getItem(key);
  } catch {
    return null;
  }
}
function set(store: () => Storage, key: string, value: string | null) {
  try {
    if (value == null) store().removeItem(key);
    else store().setItem(key, value);
  } catch {
    /* rien à garder */
  }
}
const local = () => localStorage;
const session = () => sessionStorage;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches === true ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

// ?installer=1 (lien de la page d'accueil) : gardé pour la visite, pour survivre à la connexion,
// puis retiré de l'adresse pour qu'un partage du lien ne le propage pas.
const url = new URL(window.location.href);
if (url.searchParams.has("installer")) {
  set(session, REQUEST_KEY, "1");
  url.searchParams.delete("installer");
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
}

let deferred: BeforeInstallPromptEvent | null = null;

interface InstallState {
  canPrompt: boolean;
  standalone: boolean;
  requested: boolean;
  snoozedAt: number | null;
}

const snoozed = Number(get(local, SNOOZE_KEY));
export const useInstall = create<InstallState>(() => ({
  canPrompt: false,
  standalone: isStandalone(),
  requested: get(session, REQUEST_KEY) === "1",
  snoozedAt: Number.isFinite(snoozed) && snoozed > 0 ? snoozed : null,
}));

const isIos = isIosDevice(navigator.userAgent, navigator.platform, navigator.maxTouchPoints ?? 0);

// Écouté dès le démarrage : Chrome envoie souvent l'événement avant le premier affichage.
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferred = e as BeforeInstallPromptEvent;
  useInstall.setState({ canPrompt: true });
});
window.addEventListener("appinstalled", () => {
  deferred = null;
  set(session, REQUEST_KEY, null);
  useInstall.setState({ canPrompt: false, standalone: true, requested: false });
});

/** Ouvre la fenêtre d'installation du navigateur (Android). */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = deferred;
  if (!event) return "unavailable";
  await event.prompt();
  const { outcome } = await event.userChoice;
  // Un événement ne sert qu'une fois ; Chrome en renverra un s'il peut encore proposer l'installation.
  deferred = null;
  useInstall.setState({ canPrompt: false });
  return outcome;
}

/** « Plus tard » : la carte disparaît pendant 7 jours, demande de la page d'accueil comprise. */
export function snoozeInstall() {
  const now = Date.now();
  set(local, SNOOZE_KEY, String(now));
  set(session, REQUEST_KEY, null);
  useInstall.setState({ snoozedAt: now, requested: false });
}

/** Ce qu'il faut afficher : la carte (selon les règles) et la façon d'installer. */
export function useInstallCard() {
  const s = useInstall();
  const mode = installMode(s.canPrompt, isIos);
  return {
    mode,
    standalone: s.standalone,
    requested: s.requested,
    visible: shouldShowCard({ ...s, mode, now: Date.now() }),
  };
}
